import {
  useGetCountryIsoCodesQuery,
  useGetCountriesForPhoneCodeQuery,
} from '@/graphql/generated'
import {
  ChangeEvent,
  JSX,
  memo,
  useEffect,
  useState
} from 'react'
import { AutocompleteSearch } from '../atoms'
import { normalizeDate } from '@/helpers'

const LABEL_CLASSES = "block text-[15px] font-semibold text-text mb-2.5 leading-normal pr-[50px] pl-[5px]";
const INPUT_CLASSES = "block w-full h-auto py-[14px] px-5 bg-background2 text-sm font-medium tracking-[0.02em] text-text-color rounded-md border-none transition-all duration-500 ease-in-out box-border focus:outline-none placeholder:text-gray-400";
const SELECT_CLASSES = "block w-full h-auto py-[14px] px-5 bg-background2 text-sm font-medium tracking-[0.02em] text-text-color rounded-md border-none transition-all duration-500 ease-in-out box-border focus:outline-none appearance-none";
const TEXTAREA_CLASSES = "block w-full h-auto py-[14px] px-5 bg-background2 text-sm font-medium tracking-[0.02em] text-text-color rounded-md border-none transition-all duration-500 ease-in-out box-border focus:outline-none placeholder:text-gray-400 resize-none";

type FormFieldProps = {
  label: string
  name?: string
  action?: (value: string) => void
  placeholder? : string
  readOnly?: boolean
  required?: boolean
  onChange?: (value: string | null) => void
  value?: string
  disabled?: boolean
  maxLength?: number
}

type NormalFormSelectionFieldProps<T> = {
  label: string;
  name: string;
  options: { name: string; value: T }[];
  action?: (value: T) => void;
  required?: boolean;
  noValueText?: string;
  onChange?: (value: T) => void;
  readOnly?: boolean;
  value?: T | null;
};

type NormalAutoCompleteSearchSelectionFieldProps = FormFieldProps & {
  options: { name: string, value: string }[]
}

type NormalFormCountrySelectionField = {
  label: string
  name: string
  action?: (value: string | null) => void
  required?: boolean
  onChange?: (value: string | null) => void
  value?: string
  noValueText?: string
  readOnly?: boolean
  placeholder?: string
}

const splitPhoneNumber = (
  value: string,
  countries?: { iso2: string; phoneCode?: string | null }[]
) => {
  if (!value) {
    return { codeIso2: '', phone: '' }
  }

  const parts = value.split('-')
  if (parts.length > 1 && parts[1]) {
    return {
      codeIso2: parts[0],
      phone: parts.slice(1).join('-')
    }
  }

  if (!countries || countries.length === 0) {
    return { codeIso2: '', phone: value }
  }

  const digits = value.replace(/\D/g, '')
  if (!digits) {
    return { codeIso2: '', phone: value }
  }

  let bestMatch: { iso2: string; phoneCode: string } | null = null

  for (const country of countries) {
    if (!country.phoneCode) continue
    if (digits.startsWith(country.phoneCode)) {
      if (!bestMatch || country.phoneCode.length > bestMatch.phoneCode.length) {
        bestMatch = { iso2: country.iso2, phoneCode: country.phoneCode }
      }
    }
  }

  if (!bestMatch) {
    return { codeIso2: '', phone: value }
  }

  const localPhone = digits.slice(bestMatch.phoneCode.length)

  return {
    codeIso2: bestMatch.iso2,
    phone: localPhone || ''
  }
}


export const NormalFormEmailField = memo(({
  label,
  name,
  placeholder,
  action,
  required,
  readOnly,
  value: propValue,
  onChange: propOnChange,
  maxLength,
}: FormFieldProps) => {
  const [internalValue, setInternalValue] = useState<string>(propValue ?? '')

  useEffect(() => {
    setInternalValue(propValue ?? '')
  }, [propValue])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInternalValue(newValue)
    propOnChange?.(newValue)
  }

  const handleBlur = () => {
    action?.(internalValue)
  }

  return (
    <div className="mb-[15px]">
      <label htmlFor={name} className={LABEL_CLASSES}>
        {
          required
          ? (
            `${label} *`
          )
          : label  
        }
      </label>    
      <input
        type="email"
        id={name}
        name={name}
        placeholder={placeholder}
        className={INPUT_CLASSES}
        value={internalValue}
        onChange={handleChange}
        required={required}
        onBlur={handleBlur}
        readOnly={readOnly}
        maxLength={maxLength}
      />
    </div>
  )
});

export const NormalFormPasswordField = memo(({
  label,
  name,
  placeholder,
  action,
  required,
  readOnly,
  value: propValue = '',
  onChange: propOnChange,
}: FormFieldProps) => {
  const [internalValue, setInternalValue] = useState<string>(propValue);

  useEffect(() => {
    setInternalValue(propValue)
  }, [propValue])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    if (newValue.length > 0) propOnChange?.(newValue)
    else propOnChange?.(null)
  };

  const handleBlur = () => {
    action?.(internalValue);
  };

  return (
    <div className="mb-[15px]">
      <label htmlFor={name} className={LABEL_CLASSES}>{required ? `${label} *` : label}</label>
      <input
        type="password"
        id={name}
        name={name}
        placeholder={placeholder}
        className={INPUT_CLASSES}
        value={internalValue}
        onChange={handleChange}
        required={required}
        onBlur={handleBlur}
        readOnly={readOnly}
      />
    </div>
  );
});

export const NormalFormTextField = memo(({
  label,
  name,
  placeholder,
  action,
  required,
  readOnly,
  value: propValue = '',
  onChange: propOnChange,
  maxLength,
}: FormFieldProps) => {
  const [internalValue, setInternalValue] = useState<string>(propValue);

  useEffect(() => {
    setInternalValue(propValue)
  }, [propValue])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    if (newValue.length > 0) propOnChange?.(newValue)
    else propOnChange?.(null)
  };

  const handleBlur = () => {
    action?.(internalValue);
  };

  return (
    <div className="mb-[15px]">
      <label htmlFor={name} className={LABEL_CLASSES}>{required ? `${label} *` : label}</label>
      <input
        type="text"
        id={name}
        name={name}
        placeholder={placeholder}
        className={INPUT_CLASSES}
        value={internalValue}
        onChange={handleChange}
        required={required}
        onBlur={handleBlur}
        readOnly={readOnly}
        maxLength={maxLength ?? 250}
      />
    </div>
  );
});

export const NormalFormTextArea = memo(({
  label,
  name,
  placeholder,
  action,
  required,
  value: propValue = '',
  onChange: propOnChange,
}: FormFieldProps) => {
  const [internalValue, setInternalValue] = useState<string>(propValue);

  useEffect(() => {
    setInternalValue(propValue)
  }, [propValue])

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    if (newValue.length > 0) propOnChange?.(newValue)
    else propOnChange?.(null)
  };

  const handleBlur = () => {
    action?.(internalValue);
  };

  return (
    <div className="mb-[15px]">
      <label htmlFor={name} className={LABEL_CLASSES}>{required ? `${label} *` : label}</label>
      <textarea
        id={name}
        name={name}
        placeholder={placeholder}
        className={TEXTAREA_CLASSES}
        value={internalValue}
        onChange={handleChange}
        required={required}
        onBlur={handleBlur}
        rows={8}
      />
    </div>
  );
});

export const NormalFormPhoneField = memo(({
  label,
  name,
  placeholder,
  action,
  required,
  readOnly,
  value: propValue = '',
  onChange: propOnChange,
}: FormFieldProps) => {
  const [phoneNumber, setPhoneNumber] = useState(propValue);
  const [selectedCountryIsoCode, setSelectedCountryIsoCode] = useState<{ name: string, value: string }>({ name: '', value: '' });
  
  const { data } = useGetCountriesForPhoneCodeQuery();

  useEffect(() => {
    if (!data) return
    const { codeIso2, phone } = splitPhoneNumber(propValue, data.countries)

    if (!codeIso2 && !phone) {
      setSelectedCountryIsoCode({ name: '', value: '' })
      setPhoneNumber('')
      return
    }

    const defaultPhoneCode = data.countries.find((country) => country.iso2 === codeIso2)
    if (!defaultPhoneCode) return 

    setSelectedCountryIsoCode({
      name: (defaultPhoneCode.phoneCode ? '+' + defaultPhoneCode.phoneCode + ' ' : '') + defaultPhoneCode.name,
      value: defaultPhoneCode.iso2
    })

    setPhoneNumber(phone)
  }, [propValue, data, data?.countries])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setPhoneNumber(newValue);
    if (newValue.length > 0) propOnChange?.(`${selectedCountryIsoCode.value}-${newValue}`)
    else propOnChange?.(null)
  };

  const handleCountryCodeChange = ({ name, value }:{ name: string; value: string}) => {
    setSelectedCountryIsoCode({ name, value });
    if (phoneNumber.length > 0) propOnChange?.(`${value}-${phoneNumber}`)
    else propOnChange?.(null)
  }
  /* 
  const handleCountryCodeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newCountryCode = e.target.value;
    setSelectedCountryIsoCode(newCountryCode);
    if (phoneNumber.length > 0) propOnChange?.(`${newCountryCode}-${phoneNumber}`)
    else propOnChange?.(null)
  };
  */
  const handleBlur = () => {
    action?.('');
  };

  return (
    <div className="mb-[15px]">
      <label htmlFor={name} className={LABEL_CLASSES}>{required ? `${label} *` : label}</label>
      <div className="flex items-center gap-[10px]">
        <div className="flex-1">
          <AutocompleteSearch
            items={data?.countries.map((country) => ({
              name: '+' + country.phoneCode + ' ' + country.name,
              value: country.iso2
            })) ?? []}
            onChange={handleCountryCodeChange}
            placeholder='Phone Code'
            defaultValue={selectedCountryIsoCode}
            readOnly={readOnly}
          />
        </div>
        <input
          type="text"
          id={name}
          name={name}
          placeholder={placeholder}
          className={`${INPUT_CLASSES} flex-[2]`}
          value={phoneNumber}
          onChange={handleChange}
          required={required}
          onBlur={handleBlur}
          pattern="^[0-9]{6,14}$"
          title="Insert a valid phone number between 6 to 14 digits, without any space or special characters"
          readOnly={readOnly}
        />
      </div>
    </div>
  );
})

const GenericSelectionField = <T,>({
  label,
  name,
  options,
  required,
  noValueText = '---',
  onChange: propOnchange,
  readOnly,
  value: propValue
}: NormalFormSelectionFieldProps<T>) => {
  const [internalValue, setInternalValue] = useState<T | null>(propValue ?? null);

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = options.find(opt => 
      String(opt.value) === e.target.value
    );
    if (selectedOption) {
      setInternalValue(selectedOption.value);
      propOnchange?.(selectedOption.value);
    }
  };

  useEffect(() => {
    setInternalValue(propValue ?? null)
  }, [propValue])

  return (
    <div className="mb-[15px]">
      <label htmlFor={name} className={LABEL_CLASSES}>{required ? `${label} *` : label}</label>
      <select
        id={name}
        name={name}
        className={SELECT_CLASSES}
        value={internalValue !== null ? String(internalValue) : ''}
        onChange={handleChange}
        required={required}
        disabled={readOnly}
      >
        <option value="">{noValueText}</option>
        {options.map((option, key) => (
          <option value={String(option.value)} key={key}>
            {option.name}
          </option>
        ))}
      </select>
    </div>
  );
};

export const NormalFormSelectionField = memo(GenericSelectionField) as
  <T,>(props: NormalFormSelectionFieldProps<T>) => JSX.Element

export const NormalFormDateField = memo(({
  label,
  name,
  placeholder,
  required,
  readOnly,
  value: propValue = '',
  onChange: propOnChange,
  disabled,
}: FormFieldProps) => {
  const [internalValue, setInternalValue] = useState<string>(propValue);
  const [hasChanged, setHasChanged] = useState(false)

  useEffect(() => {
    if (!hasChanged && propValue) setInternalValue(normalizeDate(propValue));
  }, [propValue, hasChanged]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    propOnChange?.(newValue ?? '')
    setHasChanged(true)
  };

  return (
    <div className="mb-[15px]">
      <label htmlFor={name} className={LABEL_CLASSES}>{required ? `${label} *` : label}</label>
      <input
        type="date"
        id={name}
        name={name}
        placeholder={placeholder}
        className={INPUT_CLASSES}
        value={internalValue}
        onChange={handleChange}
        required={required}
        readOnly={readOnly}
        disabled={disabled}
      />
    </div>
  );
});

export const NormalFormCountrySelectionField = memo(({
  label,
  name,
  required,
  onChange: propOnchange,
  value: propValue = '',
  readOnly,
  placeholder = 'Buscar pais'
}: NormalFormCountrySelectionField) => {
  const [internalValue, setInternalValue] = useState<{ name: string, value: string }>({ name: '', value: '' });
  const { data } = useGetCountryIsoCodesQuery();

  useEffect(() => {
    if (!data) return
    const defaultCountry = data.countries.find((country) => country.iso2 === propValue)
    
    setInternalValue(defaultCountry
      ? { name: defaultCountry.name, value: defaultCountry.iso2}
      : { name: '', value: '' })
  }, [data, data?.countries, propValue]) 

  const handleChange = (props: { name: string, value: string }) => {
    setInternalValue(props);
    propOnchange?.(props.value);
  }

  return (
    <div className="mb-[15px]">
      <label htmlFor={name} className={LABEL_CLASSES}>{required ? `${label} *` : label}</label>
      <AutocompleteSearch
        items={data?.countries.map((country) => ({
          name: country.name,
          value: country.iso2
        })) ?? []}
        placeholder={placeholder}
        onChange={handleChange}
        defaultValue={internalValue}
        readOnly={readOnly}
      />
    </div>
  );
});

export const NormalFormNumberField = memo(({
  label,
  name,
  action,
  required,
  readOnly,
  value: propValue = '0',
  onChange: propOnChange,
}: Omit<FormFieldProps, 'placeholder'>) => {
  const [internalValue, setInternalValue] = useState<string>(propValue);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    setInternalValue(newValue);
    
    if (newValue.length > 0) propOnChange?.(newValue)
    else propOnChange?.('0')
  };

  const handleBlur = () => {
    action?.(internalValue);
  };

  return (
    <div className="mb-[15px]">
      <label htmlFor={name} className={LABEL_CLASSES}>{required ? `${label} *` : label}</label>
      <input
        type="number"
        id={name}
        name={name}
        className={INPUT_CLASSES}
        value={internalValue}
        onChange={handleChange}
        required={required}
        onBlur={handleBlur}
        readOnly={readOnly}
      />
    </div>
  );
});


export const NormalAutoCompleteSearchSelectionField = memo(({
  label,
  name,
  placeholder,
  required,
  readOnly,
  options,
  value: propValue,
  onChange: propOnChange,
}: NormalAutoCompleteSearchSelectionFieldProps) => {
  const [internalValue, setInternalValue] = useState<{ name: string; value: string }>({ name: '', value: ''})

  useEffect(() => {
    if (!propValue || options.length === 0) return
    const defaultValue = options.find((option) => option.value === propValue)

    setInternalValue(defaultValue
      ? { name: defaultValue.name, value: defaultValue.value }
      : { name: '', value: ''}
    )
  }, [propValue, options])

  const handleChange = (props: { name: string, value: string }) => {
    setInternalValue(props);
    propOnChange?.(props.value);
  }
  
  return (
    <div className="mb-[15px]">
      <label htmlFor={name} className={LABEL_CLASSES}>
        {
          required
          ? (
            `${label} *`
          )
          : label  
        }
      </label>
      <AutocompleteSearch
        items={options.map((option) => ({
          name: option.name,
          value: option.value,
        })) ?? []}
        placeholder={placeholder}
        onChange={handleChange}
        defaultValue={internalValue}
        readOnly={readOnly}
        name={name}
      />
    </div>
  )
});

type NormalCheckBoxFieldProps = {
  label: string
  name?: string
  required?: boolean
  onChange?: (value: boolean) => void
  checked?: boolean
  defaultChecked?: boolean
}

export const NormalCheckBoxField = memo(({
  label,
  name,
  required,
  onChange,
  checked,
  defaultChecked = false,
}: NormalCheckBoxFieldProps) => {
  const [internalState, setInternalState] = useState(defaultChecked)

  const onChangeHandle = () => {
    if (checked) return
    setInternalState((prev) => !prev)
  }

  useEffect(() => {
    if(!onChange) return

    onChange(internalState)
  }, [internalState, setInternalState]  )
    return (
      <div className="flex items-center gap-2 mb-4">
        <label htmlFor={name} className="text-[15px] font-semibold text-text leading-normal order-2">
          {
            required
            ? label + ' *'
            : label
          }
        </label>
        <input
          type="checkbox"
          required={required}
          name={name}
          id={name}
          defaultChecked={internalState}
          checked={checked}
          onChange={onChangeHandle}
          className="w-4 h-4 rounded text-primary1 focus:ring-primary1 order-1"
        />
      </div>
    )
})
